/**
 * <XtsDimensions> — Renders dimension markers per Part.
 *
 * Per Part:
 *   - thin tube along the entire centerline
 *   - tick markers at every module boundary (Tick / Diamond / None per
 *     `display.dimensionMarkerOptions` — same look as before)
 *   - intermediate tick markers every `intermediateStepMm` (when > 0),
 *     visually subordinate (smaller, alt color)
 *   - billboarded numeric labels at each tick when `showValues` is true
 *
 * `invertDimensions` iterates module boundaries from the END of the part
 * instead of the start.
 */

import React from 'react';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import {
  buildDimensionTicks,
  formatDimensionValue,
  type DimensionTick,
} from '../../geometry/dimensionTicks.js';
import {
  sampleChainPoint,
  sampleChainRange,
} from '../../geometry/sampleChain.js';
import { chainToUserPositionMm } from '../../geometry/positionFrame.js';
import { PathTube } from './PathTube.js';
import { PathMarker } from './PathMarker.js';
import { DimensionLabel } from './DimensionLabel.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import type {
  DisplayOptions,
  MarkerShape,
  PartTransformation,
  PositionFrame,
  RailSystem,
} from '../../types.js';

interface XtsDimensionsProps {
  chainsByPartOid: Map<number, BuiltChain>;
  /** Per-part transforms keyed by `PartConfig.globalNumber`. */
  partTransformsByPartOid?: Map<number, PartTransformation | undefined>;
  /**
   * Active rail system on the parent XPU. Drives the default lateral
   * offset: the Beckhoff aluminium rail is wider than the Hepco GFX
   * profile, so dimensions need to step further out (80 mm vs 40 mm)
   * to clear it. Consumers can still override via
   * `display.dimensionOptions.lateralDisplacementMm`.
   */
  railSystem?: RailSystem;
  display?: DisplayOptions;
  /**
   * XPU position frame. Tick mm values are reported in user-frame
   * (`chainToUserPositionMm`) so a label that reads "300 mm" matches a
   * mover whose `partPositionMm` is 300, regardless of `direction` /
   * `originMm`. Tick *placement* in 3D space is unchanged — only the
   * label text follows the user's frame.
   */
  positionFrame?: PositionFrame;
}

const XtsDimensionsImpl: React.FC<XtsDimensionsProps> = ({
  chainsByPartOid,
  partTransformsByPartOid,
  railSystem,
  display,
  positionFrame,
}) => {
  const dim = display?.dimensionOptions;
  const thickness = dim?.thicknessMm ?? 4;
  // Default overlay placement: on the table plane (z = 0), shifted
  // lateral so the dimensions sit beside the mover line instead of
  // underneath it. Beckhoff rail = 80 mm clearance, Hepco GFX = 40 mm.
  const liftZ = dim?.displacementMm ?? 0;
  const defaultLateralMm = railSystem === 'HepcoGfx' ? 40 : 80;
  const lateralMm = dim?.lateralDisplacementMm ?? defaultLateralMm;
  const invert = display?.invertDimensions ?? false;
  const color = dim?.textOptions?.color ?? '#88AAFF';
  const showValues = dim?.showValues ?? false;
  const showIntermediateValues = dim?.showIntermediateValues ?? true;
  const intermediateStepMm = dim?.intermediateStepMm ?? 0;
  const valueMode = dim?.valueMode ?? 'partPosition';
  const labelSize = dim?.textOptions?.sizeMm ?? 30;
  const fontFamily = dim?.textOptions?.fontFamily;
  const intermediateMarker = dim?.intermediateMarkerOptions;

  return (
    <group name="xts-dimensions">
      {Array.from(chainsByPartOid.entries()).map(([oid, chain]) => (
        <PartTransformGroup
          key={oid}
          name={`dim-wrap-part-${oid}`}
          partTransformation={partTransformsByPartOid?.get(oid)}
        >
          <PartDimensions
            chain={chain}
            thickness={thickness}
            liftZ={liftZ}
            lateralMm={lateralMm}
            invert={invert}
            color={color}
            showValues={showValues}
            showIntermediateValues={showIntermediateValues}
            intermediateStepMm={intermediateStepMm}
            intermediateShape={intermediateMarker?.shape ?? 'Tick'}
            intermediateSizeMm={intermediateMarker?.sizeMm}
            valueMode={valueMode}
            labelSize={labelSize}
            fontFamily={fontFamily}
            positionFrame={positionFrame}
          />
        </PartTransformGroup>
      ))}
    </group>
  );
};

export const XtsDimensions = React.memo(XtsDimensionsImpl);
XtsDimensions.displayName = 'XtsDimensions';

const PartDimensions: React.FC<{
  chain: BuiltChain;
  thickness: number;
  liftZ: number;
  lateralMm: number;
  invert: boolean;
  color: string;
  showValues: boolean;
  showIntermediateValues: boolean;
  intermediateStepMm: number;
  intermediateShape: MarkerShape;
  intermediateSizeMm?: number;
  valueMode: 'partPosition' | 'fromModule' | 'both';
  labelSize: number;
  fontFamily?: string;
  positionFrame?: PositionFrame;
}> = ({
  chain,
  thickness,
  liftZ,
  lateralMm,
  invert,
  color,
  showValues,
  showIntermediateValues,
  intermediateStepMm,
  intermediateShape,
  intermediateSizeMm,
  valueMode,
  labelSize,
  fontFamily,
  positionFrame,
}) => {
  // Continuous tube along the whole part. Apply lateral + vertical offsets
  // so the tube can sit beside / above / below the centerline.
  const tubePoints = sampleChainRange(
    chain,
    0,
    chain.trackLengthMm,
    20,
    liftZ,
    lateralMm,
  );
  const ticks = buildDimensionTicks(chain, { invert, intermediateStepMm });

  const boundarySize = Math.max(thickness * 2, 8);
  const intSize = intermediateSizeMm ?? boundarySize * 0.6;
  // Slightly desaturate / dim the intermediate marker so it stays visually
  // subordinate to the boundary ticks.
  const intColor = mixWithBlack(color, 0.35);

  type Resolved = {
    tick: DimensionTick;
    sample: NonNullable<ReturnType<typeof sampleChainPoint>>;
  };
  const resolved: Resolved[] = [];
  for (const t of ticks) {
    const sample = sampleChainPoint(chain, t.positionMm, liftZ, lateralMm);
    if (sample) resolved.push({ tick: t, sample });
  }

  return (
    <>
      <PathTube
        points={tubePoints}
        thicknessMm={thickness}
        color={color}
        opacity={0.7}
      />
      {resolved.map(({ tick, sample }, i) => {
        const isBoundary = tick.kind === 'boundary';
        const showThisLabel =
          showValues && (isBoundary || showIntermediateValues);
        // Report the tick's mm value in the host's user-frame so the
        // label matches the convention every other prop uses
        // (mover.partPositionMm, station.startPositionOnPart, …). For
        // direction='positive', originMm=0 this is identity, so legacy
        // setups read identically.
        const userTickMm = chainToUserPositionMm(tick.positionMm, positionFrame);
        const labelText = formatDimensionValue(
          { ...tick, positionMm: userTickMm },
          valueMode,
        );
        return (
          <React.Fragment key={i}>
            <PathMarker
              position={sample.position}
              tangent={sample.tangent}
              shape={isBoundary ? 'Tick' : intermediateShape}
              sizeMm={isBoundary ? boundarySize : intSize}
              color={isBoundary ? color : intColor}
            />
            {showThisLabel && (
              <DimensionLabel
                positionMm={[
                  sample.position.x,
                  sample.position.y,
                  sample.position.z,
                ]}
                text={labelText}
                sizeMm={isBoundary ? labelSize : labelSize * 0.8}
                color={isBoundary ? color : intColor}
                fontFamily={fontFamily}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

/** Mix a hex CSS color with black by `t ∈ [0,1]` (0 → original, 1 → black). */
function mixWithBlack(hex: string, t: number): string {
  if (!hex.startsWith('#') || (hex.length !== 7 && hex.length !== 4))
    return hex;
  const expand = hex.length === 4
    ? '#' + hex.slice(1).split('').map((c) => c + c).join('')
    : hex;
  const r = parseInt(expand.slice(1, 3), 16);
  const g = parseInt(expand.slice(3, 5), 16);
  const b = parseInt(expand.slice(5, 7), 16);
  const k = (v: number) => Math.max(0, Math.min(255, Math.round(v * (1 - t))));
  return `#${k(r).toString(16).padStart(2, '0')}${k(g).toString(16).padStart(2, '0')}${k(b).toString(16).padStart(2, '0')}`;
}
